import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import Layout from '../components/layout/Layout';
import MobileCardList, { TableColumn } from '../components/common/MobileCardList';
import ResponsiveSelect from '../components/common/ResponsiveSelect';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import {
  CreateTenantPayload,
  Tenant,
  tenantManagementService,
  UpdateTenantPayload,
} from '../services/tenantManagementService';

const INITIAL_CREATE_FORM: CreateTenantPayload = {
  name: '',
  status: 'active',
};

export default function TenantControlPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { applySession, hasPermission, user } = useAuth();
  const { showToast } = useToast();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTenant, setEditingTenant] = useState<Tenant | null>(null);
  const [createForm, setCreateForm] = useState<CreateTenantPayload>(
    INITIAL_CREATE_FORM,
  );
  const [editForm, setEditForm] = useState<UpdateTenantPayload>({
    name: '',
    status: 'active',
  });

  const canManageTenants = hasPermission('tenants:manage');
  const canImpersonateTenant = hasPermission('tenants:impersonate');

  const { data: tenants = [], isLoading } = useQuery({
    queryKey: ['tenant-control', 'tenants'],
    queryFn: tenantManagementService.getTenants,
  });

  const sortedTenants = useMemo(
    () =>
      [...tenants].sort((left, right) =>
        left.name.localeCompare(right.name, undefined, { sensitivity: 'base' }),
      ),
    [tenants],
  );

  useEffect(() => {
    if (!editingTenant) {
      return;
    }

    setEditForm({
      name: editingTenant.name,
      status: editingTenant.status,
    });
  }, [editingTenant]);

  const createTenantMutation = useMutation({
    mutationFn: tenantManagementService.createTenant,
    onSuccess: () => {
      showToast('Tenant created successfully', 'success');
      setShowCreateModal(false);
      setCreateForm(INITIAL_CREATE_FORM);
      queryClient.invalidateQueries({ queryKey: ['tenant-control', 'tenants'] });
    },
    onError: (error: any) => {
      showToast(error?.message || 'Failed to create tenant', 'error');
    },
  });

  const updateTenantMutation = useMutation({
    mutationFn: ({
      tenantId,
      payload,
    }: {
      tenantId: number;
      payload: UpdateTenantPayload;
    }) => tenantManagementService.updateTenant(tenantId, payload),
    onSuccess: () => {
      showToast('Tenant updated successfully', 'success');
      setEditingTenant(null);
      queryClient.invalidateQueries({ queryKey: ['tenant-control', 'tenants'] });
    },
    onError: (error: any) => {
      showToast(error?.message || 'Failed to update tenant', 'error');
    },
  });

  const impersonateMutation = useMutation({
    mutationFn: tenantManagementService.impersonateTenant,
    onSuccess: (session) => {
      applySession(session);
      showToast('Tenant session activated', 'success');
      navigate('/dashboard');
    },
    onError: (error: any) => {
      showToast(error?.message || 'Failed to switch tenant session', 'error');
    },
  });

  const handleCreateTenant = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    createTenantMutation.mutate({
      name: createForm.name.trim(),
      status: createForm.status || 'active',
    });
  };

  const handleUpdateTenant = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!editingTenant) {
      return;
    }

    updateTenantMutation.mutate({
      tenantId: editingTenant.id,
      payload: {
        name: editForm.name?.trim() || undefined,
        status: editForm.status,
      },
    });
  };

  // Shared by the desktop table and the mobile card list
  const tenantColumns: TableColumn[] = [
    {
      key: 'name',
      label: 'Tenant',
      render: (value, tenant) => (
        <div>
          <div className="font-medium text-gray-900">{value}</div>
          <div className="text-sm text-gray-500">Tenant ID: {tenant.id}</div>
        </div>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (value) => (
        <span
          className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${
            value === 'active'
              ? 'bg-green-100 text-green-800 border border-green-200'
              : 'bg-gray-100 text-gray-700 border border-gray-200'
          }`}
        >
          {value}
        </span>
      ),
    },
    {
      key: 'createdAt',
      label: 'Created',
      render: (value) => new Date(value).toLocaleString(),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (_, tenant) => {
        const isCurrentTenant = user?.tenantId === tenant.id;
        return (
          <div className="flex flex-wrap gap-2 justify-end">
            {canManageTenants && (
              <button
                onClick={() => setEditingTenant(tenant)}
                className="px-3 py-1.5 rounded-lg border-2 border-brand-200 text-brand-700 hover:bg-brand-50 text-sm font-medium transition-colors duration-200"
              >
                Edit
              </button>
            )}
            {canImpersonateTenant && (
              <button
                onClick={() => impersonateMutation.mutate(tenant.id)}
                disabled={impersonateMutation.isPending || tenant.status !== 'active'}
                title={tenant.status !== 'active' ? 'Cannot impersonate an inactive tenant' : undefined}
                className="px-3 py-1.5 rounded-lg bg-primary-600 text-white hover:bg-primary-700 text-sm disabled:opacity-50 transition-colors duration-200"
              >
                {isCurrentTenant ? 'Refresh Session' : 'Login As Tenant'}
              </button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <Layout>
      <div className="min-h-screen px-1 py-4 lg:p-4 bg-gradient-to-br from-gray-50 to-gray-100 scrollbar-hide" style={{ overflowY: 'auto' }}>
        {/* Header with enhanced shadow and gradient */}
        <div className="mb-4 transform transition-all duration-300 hover:scale-[1.01]">
          <div className="flex flex-wrap items-center justify-between gap-3 bg-gradient-to-r from-brand-100 to-brand-50 p-5 rounded-xl shadow-lg border-2 border-brand-300 hover:shadow-xl transition-shadow duration-300">
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-gradient-to-br from-primary-600 to-primary-700 rounded-xl shadow-md transform transition-transform duration-300 hover:scale-110 hover:rotate-3">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-900">Tenant Control</h2>
                <p className="text-gray-600 mt-1">
                  Manage customer tenants centrally and start a tenant-scoped super admin session whenever you need to work inside a client environment.
                </p>
              </div>
            </div>
            {canManageTenants && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="w-full lg:w-auto justify-center px-6 py-3 min-h-[44px] lg:min-h-0 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-xl hover:from-primary-700 hover:to-primary-800 transition-all duration-300 font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center space-x-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span className="whitespace-nowrap">Create Tenant</span>
              </button>
            )}
          </div>
        </div>

        {user?.isImpersonating && user.tenant && (
          <div className="mb-4 rounded-2xl border-2 border-amber-300 bg-gradient-to-r from-amber-50 to-amber-100 px-6 py-5 shadow-lg transform transition-all duration-300 hover:shadow-xl">
            <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-amber-200 rounded-lg">
                  <svg className="w-5 h-5 text-amber-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-sm font-semibold uppercase tracking-wide text-amber-800">
                    Active Tenant Session
                  </h2>
                  <p className="mt-1 text-amber-900">
                    You are currently operating inside <span className="font-semibold">{user.tenant.name}</span> using your super admin account.
                  </p>
                </div>
              </div>
              <div className="text-sm font-semibold text-amber-700 bg-amber-200 px-3 py-1 rounded-lg">
                Tenant ID: {user.tenantId}
              </div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-xl border-2 border-gray-300 overflow-hidden transform transition-all duration-300 hover:shadow-2xl hover:border-primary-300">
          <div className="p-5 bg-gradient-to-r from-gray-50 to-white border-b-2 border-gray-300">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <svg className="w-6 h-6 text-primary-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Tenants</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Platform-wide customer directory with tenant-context access.
                  </p>
                </div>
              </div>
              <span className="text-sm font-semibold text-gray-600 bg-gray-100 px-3 py-1 rounded-lg">
                {sortedTenants.length} {sortedTenants.length === 1 ? 'tenant' : 'tenants'}
              </span>
            </div>
          </div>

          {isLoading ? (
            <div className="p-12 text-center">
              <div className="relative inline-block">
                <div className="animate-spin rounded-full h-16 w-16 border-4 border-brand-200 border-t-blue-600 mx-auto mb-4"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <svg className="w-8 h-8 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
              </div>
              <p className="text-gray-800 font-semibold text-lg">Loading tenants...</p>
              <p className="text-gray-500 text-sm mt-2">Please wait while we fetch tenant data</p>
            </div>
          ) : sortedTenants.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <p className="text-gray-600 font-semibold">No tenants found yet.</p>
              <p className="text-gray-500 text-sm mt-2">Create your first tenant to get started.</p>
            </div>
          ) : (
            <>
            <div className="p-3 lg:hidden">
              <MobileCardList columns={tenantColumns} data={sortedTenants} />
            </div>
            <div className="hidden lg:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gradient-to-r from-gray-100 to-gray-50">
                  <tr>
                    <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-700">Tenant</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-700">Status</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-700">Created</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {sortedTenants.map((tenant) => {
                    const isCurrentTenant = user?.tenantId === tenant.id;

                    return (
                      <tr key={tenant.id} className="hover:bg-gradient-to-r hover:from-brand-50 hover:to-transparent transition-all duration-300">
                        <td className="px-5 py-4">
                          <div className="font-medium text-gray-900">{tenant.name}</div>
                          <div className="text-sm text-gray-500">Tenant ID: {tenant.id}</div>
                        </td>
                        <td className="px-5 py-4">
                          <span
                            className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${
                              tenant.status === 'active'
                                ? 'bg-green-100 text-green-800 border border-green-200'
                                : 'bg-gray-100 text-gray-700 border border-gray-200'
                            }`}
                          >
                            {tenant.status}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-sm text-gray-500">
                          {new Date(tenant.createdAt).toLocaleString()}
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex flex-wrap gap-2">
                            {canManageTenants && (
                              <button
                                onClick={() => setEditingTenant(tenant)}
                                className="px-3 py-1.5 rounded-lg border-2 border-brand-200 text-brand-700 hover:bg-brand-50 text-sm font-medium transition-colors duration-200"
                              >
                                Edit
                              </button>
                            )}
                            {canImpersonateTenant && (
                              <button
                                onClick={() => impersonateMutation.mutate(tenant.id)}
                                disabled={impersonateMutation.isPending}
                                className="px-3 py-1.5 rounded-lg bg-primary-600 text-white hover:bg-primary-700 text-sm disabled:opacity-50 transition-colors duration-200"
                              >
                                {isCurrentTenant ? 'Refresh Session' : 'Login As Tenant'}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            </>
          )}
        </div>
      </div>

      {showCreateModal && (
        <div className="fixed inset-0 bg-black/40 flex items-end lg:items-center justify-center z-50 p-3 lg:p-4">
          <div className="w-full max-w-lg rounded-2xl lg:rounded-2xl bg-white shadow-xl max-h-[85vh] overflow-y-auto animate-slide-up lg:animate-none">
            <form onSubmit={handleCreateTenant} className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">Create Tenant</h2>
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  x
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tenant Name</label>
                <input
                  required
                  value={createForm.name}
                  onChange={(event) =>
                    setCreateForm((current) => ({
                      ...current,
                      name: event.target.value,
                    }))
                  }
                  placeholder="e.g. Acme Logistics"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <ResponsiveSelect
                  options={[
                    { value: 'active', label: 'Active' },
                    { value: 'inactive', label: 'Inactive' },
                  ]}
                  value={createForm.status ?? 'active'}
                  onChange={(val) =>
                    setCreateForm((current) => ({
                      ...current,
                      status: val,
                    }))
                  }
                  includeEmpty={false}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createTenantMutation.isPending}
                  className="px-4 py-2 rounded-lg bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50"
                >
                  {createTenantMutation.isPending ? 'Creating...' : 'Create Tenant'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editingTenant && (
        <div className="fixed inset-0 bg-black/40 flex items-end lg:items-center justify-center z-50 p-3 lg:p-4">
          <div className="w-full max-w-lg rounded-2xl lg:rounded-2xl bg-white shadow-xl max-h-[85vh] overflow-y-auto animate-slide-up lg:animate-none">
            <form onSubmit={handleUpdateTenant} className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Edit Tenant</h2>
                  <p className="text-sm text-gray-500 mt-1">Tenant ID: {editingTenant.id}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setEditingTenant(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  x
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tenant Name</label>
                <input
                  required
                  value={editForm.name ?? ''}
                  onChange={(event) =>
                    setEditForm((current) => ({
                      ...current,
                      name: event.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <ResponsiveSelect
                  options={[
                    { value: 'active', label: 'Active' },
                    { value: 'inactive', label: 'Inactive' },
                  ]}
                  value={editForm.status ?? 'active'}
                  onChange={(val) =>
                    setEditForm((current) => ({
                      ...current,
                      status: val,
                    }))
                  }
                  includeEmpty={false}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setEditingTenant(null)}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={updateTenantMutation.isPending}
                  className="px-4 py-2 rounded-lg bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50"
                >
                  {updateTenantMutation.isPending ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
